/**
 * UpsellBanner - Componente de Upsell Automático Enhanced
 * Mostra ofertas contextuais baseadas no uso e plano do utilizador
 * 
 * TIPOS DE UPSELL:
 * - starter_to_premium: Upgrade de plano
 * - starter_to_8k / premium_to_8k: Upsell Desafio 8K
 * - 8k_to_maintenance: Continuidade pós-8K
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { X, Sparkles, ArrowRight, Crown, Flame, Gift, Clock, Zap, Trophy, Star } from 'lucide-react';

const UpsellBanner = ({ position = 'bottom', context = 'global' }) => {
  const [upsell, setUpsell] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkUpsells = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLoading(false);
          return;
        }

        // Verificar se já foi mostrado hoje
        const lastShown = localStorage.getItem('upsell_last_shown');
        const today = new Date().toDateString();
        if (lastShown === today && context === 'global') {
          setLoading(false);
          return;
        }

        const response = await axios.get(`${API}/resources/upsell-check`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.has_upsells && response.data.upsells.length > 0) {
          setUpsell(response.data.upsells[0]);
          if (context === 'global') {
            localStorage.setItem('upsell_last_shown', today);
          }
        }
      } catch (error) {
        console.error('Error checking upsells:', error);
      } finally {
        setLoading(false);
      }
    };

    checkUpsells();
  }, [context]);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('upsell_dismissed_at', new Date().toISOString());
  };

  const handleAction = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Para 8K, redirecionar para página de upsell dedicada
      if (upsell.target_product === 'challenge_8k') {
        window.location.href = '/challenge-8k-upsell';
        return;
      }
      
      if (upsell.target_product) {
        // Comprar produto (8K)
        const response = await axios.post(`${API}/payments/create-checkout-session`, {
          product: upsell.target_product,
          type: 'one_time',
          discount_code: upsell.discount > 0 ? 'UPSELL' + upsell.discount : null
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        }
      } else if (upsell.target_plan) {
        // Upgrade de plano - ir para página de planos
        window.location.href = '/subscription-plans';
      }
    } catch (error) {
      console.error('Error processing upsell:', error);
      // Fallback - ir para página de planos
      window.location.href = '/subscription-plans';
    }
  };

  if (loading || !upsell || dismissed) return null;

  const getIcon = () => {
    switch (upsell.type) {
      case 'premium_to_8k':
      case 'starter_to_8k':
        return <Trophy className="w-6 h-6" />;
      case 'starter_to_premium':
        return <Crown className="w-6 h-6" />;
      case '8k_to_maintenance':
        return <Gift className="w-6 h-6" />;
      default:
        return <Sparkles className="w-6 h-6" />;
    }
  };

  const getGradient = () => {
    switch (upsell.type) {
      case 'premium_to_8k':
      case 'starter_to_8k':
        return 'from-amber-500/30 to-orange-600/30 border-amber-400/50';
      case 'starter_to_premium':
        return 'from-purple-500/30 to-pink-500/30 border-purple-400/50';
      case '8k_to_maintenance':
        return 'from-blue-500/30 to-cyan-500/30 border-blue-400/50';
      default:
        return 'from-[#a3e635]/30 to-green-500/30 border-[#a3e635]/50';
    }
  };

  const getButtonStyle = () => {
    switch (upsell.type) {
      case 'premium_to_8k':
      case 'starter_to_8k':
        return 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white';
      case 'starter_to_premium':
        return 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white';
      case '8k_to_maintenance':
        return 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white';
      default:
        return 'bg-[#a3e635] hover:bg-[#84cc16] text-black';
    }
  };

  // Check if this is an urgent upsell (8K expiring)
  const isUrgent = upsell.urgency === 'high';
  const is8KUpsell = upsell.type?.includes('8k') && !upsell.type?.includes('maintenance');

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: position === 'bottom' ? 50 : -50, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: position === 'bottom' ? 50 : -50, scale: 0.95 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className={`fixed ${position === 'bottom' ? 'bottom-16 sm:bottom-4' : 'top-20'} left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-lg`}
      >
        <Card className={`bg-gradient-to-r ${getGradient()} backdrop-blur-xl border-2 shadow-2xl overflow-hidden`}>
          {/* Animated background effect for 8K upsells */}
          {is8KUpsell && (
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-amber-500/20 rounded-full blur-2xl animate-pulse" />
              <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-orange-500/20 rounded-full blur-2xl animate-pulse delay-300" />
            </div>
          )}
          
          <div className="relative p-4">
            {/* Close button */}
            <button 
              onClick={handleDismiss}
              className="absolute top-2 right-2 p-1 text-gray-400 hover:text-white rounded-full hover:bg-white/10 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            
            {/* Urgency badge */}
            {isUrgent && (
              <div className="absolute -top-1 left-4 px-3 py-1 bg-red-500 rounded-b-lg text-xs font-bold text-white flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {upsell.days_remaining} dias restantes!
              </div>
            )}
            
            <div className="flex items-start gap-4 mt-2">
              {/* Icon */}
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${
                is8KUpsell 
                  ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg shadow-amber-500/30' 
                  : 'bg-white/10 text-white'
              }`}>
                {getIcon()}
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                {/* Badge */}
                {is8KUpsell && (
                  <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 rounded-full text-amber-300 text-xs font-medium mb-2">
                    <Star className="w-3 h-3" />
                    Oferta Especial
                  </div>
                )}
                
                <p className="text-sm text-white/90 mb-3 leading-relaxed pr-4">{upsell.message}</p>
                
                {/* Price section */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex items-baseline gap-1.5">
                    {upsell.discount > 0 && (
                      <span className="text-sm text-gray-400 line-through">€{upsell.price}</span>
                    )}
                    <span className="text-2xl font-bold text-white">
                      €{upsell.final_price || upsell.price}
                    </span>
                    {upsell.target_plan && (
                      <span className="text-xs text-gray-400">/mês</span>
                    )}
                  </div>
                  
                  {upsell.discount > 0 && (
                    <span className="px-2 py-1 bg-green-500/30 text-green-300 text-xs font-bold rounded-full border border-green-500/30">
                      -{upsell.discount}%
                    </span>
                  )}
                </div>
                
                {/* Features preview for 8K */}
                {is8KUpsell && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {['Body Twin', 'Coaching IA', 'PDFs', 'Comunidade VIP'].map((feature, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 bg-white/10 rounded-full text-white/70">
                        {feature}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* CTA Button */}
            <Button
              onClick={handleAction}
              className={`w-full mt-2 font-bold py-3 ${getButtonStyle()} shadow-lg`}
            >
              {upsell.cta}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
};

export default UpsellBanner;
